import { Link } from 'react-router-dom';
import Reveal from '@/components/feature/Reveal';
import { SectionWrapper, SectionHeading } from '@/components/feature/SectionHeading';
import FAQAccordion from '@/components/feature/FAQAccordion';
import CTASection from '@/components/feature/CTASection';

export interface ServiceContent {
  tag: string;
  title: string;
  subtitle: string;
  description: string;
  icon: string;
  heroColor?: {
    blob1: string;
    blob2: string;
    accent: string;
    glow: string;
  };
  aboutTitle: string;
  aboutParagraphs: string[];
  challenges: string[];
  featuresTitle: string;
  features: { icon: string; title: string; items: string[] }[];
  valueTitle: string;
  value: string[];
  whyTitle: string;
  why: string[];
  casesTitle?: string;
  casesDescription?: string;
  cases?: { icon: string; title: string; description: string }[];
  flowTitle?: string;
  flow: { step: string; title: string; items: string[] }[];
  extra?: { title: string; description: string }[];
  faq?: { q: string; a: string }[];
  cta: { title: string; description: string; buttonText: string; serviceKey: string };
}

export default function ServiceLayout({ content }: { content: ServiceContent }) {
  const hc = content.heroColor;
  const blob1Bg = hc?.blob1 ?? 'linear-gradient(135deg, #7c3aed, #a855f7)';
  const blob2Bg = hc?.blob2 ?? 'linear-gradient(135deg, #a855f7, #7c3aed)';
  const contactTo = `/contact/?service=${encodeURIComponent(content.cta.serviceKey)}`;
  const casesTitle = content.casesTitle ?? '導入事例';
  const flowTitle = content.flowTitle ?? '導入までの流れ';

  return (
    <>
      {/* 1. Hero */}
      <section className="relative pt-32 md:pt-40 pb-20 md:pb-28 px-4 md:px-6 overflow-hidden bg-[#0d0d1a]">
        <div
          className="blob w-[500px] h-[500px] top-0 right-0"
          style={{ background: blob1Bg, opacity: 0.2 }}
        ></div>
        <div
          className="blob w-[400px] h-[400px] bottom-0 left-0"
          style={{ background: blob2Bg, opacity: 0.16, animationDelay: '-8s' }}
        ></div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#1e1e3f] border border-[#3b3b6b]">
            <span className="w-2 h-2 rounded-full bg-[#a855f7]"></span>
            <span className="text-xs font-medium tracking-[0.22em] uppercase text-[#a78bfa]">{content.tag}</span>
          </div>
          <h1 className="mt-5 text-3xl md:text-5xl font-extrabold text-white leading-tight">{content.title}</h1>
          <p className="mt-4 text-lg md:text-xl font-semibold text-[#e8e8f0]">{content.subtitle}</p>
          <p className="mt-5 text-[#a0a0b0] text-base leading-relaxed max-w-2xl mx-auto">{content.description}</p>
          <div className="mt-8 flex justify-center">
            <Link
              to={contactTo}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl h-12 px-8 text-sm font-semibold bg-gradient-to-r from-[#7c3aed] to-[#a855f7] text-white transition-all duration-300 hover:brightness-110 hover:scale-[1.02] hover:shadow-[0_14px_30px_rgba(124,58,237,0.4)]"
            >
              {content.cta.buttonText}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* 2. こんなお悩みありませんか？ */}
      <SectionWrapper>
        <SectionHeading eyebrow="Challenges" title="こんなお悩みありませんか？" />
        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
          {content.challenges.map((c, i) => (
            <Reveal key={c} delay={i * 40}>
              <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-5 flex items-start gap-4 group h-full transition-all duration-300 hover:-translate-y-1 hover:border-[#7c3aed] hover:shadow-[0_0_20px_rgba(124,58,237,0.15)]">
                <span className="w-8 h-8 shrink-0 flex items-center justify-center text-[#a855f7]">
                  <i className="ri-close-circle-line text-xl"></i>
                </span>
                <p className="text-sm text-[#c0c0d0] leading-relaxed">{c}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </SectionWrapper>

      {/* 3. ○○とは */}
      <SectionWrapper className="bg-[#12121f]">
        <Reveal className="max-w-4xl mx-auto">
          <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-3xl p-8 md:p-12">
            <div className="flex items-center gap-2 mb-5">
              <span className="inline-flex items-center px-3.5 py-1.5 rounded-full bg-[#1e1e3f] border border-[#3b3b6b] text-[#a78bfa] text-xs font-medium tracking-[0.18em] uppercase">
                Overview
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-white leading-snug">{content.aboutTitle}</h2>
            {content.aboutParagraphs.map((p, i) => (
              <p key={i} className={i === 0 ? 'mt-6 text-[#a0a0b0] text-base md:text-lg leading-loose' : 'mt-4 text-[#a0a0b0] text-base md:text-lg leading-loose'}>
                {p}
              </p>
            ))}
          </div>
        </Reveal>
      </SectionWrapper>

      {/* 4. ○○でできること */}
      <SectionWrapper>
        <SectionHeading eyebrow="Features" title={content.featuresTitle} />
        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
          {content.features.map((d, i) => (
            <Reveal key={d.title} delay={i * 60}>
              <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-8 h-full group transition-all duration-300 hover:-translate-y-1 hover:border-[#7c3aed] hover:shadow-[0_0_20px_rgba(124,58,237,0.2)]">
                <div className="flex items-center gap-4">
                  <div className="icon-outline shrink-0">
                    <i className={d.icon}></i>
                  </div>
                  <h3 className="text-lg font-extrabold text-white">{d.title}</h3>
                </div>
                <ul className="mt-5 space-y-2.5">
                  {d.items.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-[#a0a0b0] leading-relaxed">
                      <span className="w-1.5 h-1.5 mt-2 rounded-full bg-[#a855f7] shrink-0"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </SectionWrapper>

      {/* 5. 導入後に得られる価値 */}
      <SectionWrapper className="bg-[#12121f]">
        <SectionHeading eyebrow="Value" title={content.valueTitle} />
        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
          {content.value.map((v, i) => (
            <Reveal key={v} delay={i * 40}>
              <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-5 flex items-start gap-4 group h-full transition-all duration-300 hover:border-[#7c3aed]">
                <span className="w-6 h-6 shrink-0 rounded-full bg-[#7c3aed]/20 text-[#a855f7] flex items-center justify-center mt-0.5">
                  <i className="ri-check-line text-sm"></i>
                </span>
                <p className="text-sm text-[#c0c0d0] leading-relaxed">{v}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </SectionWrapper>

      {/* 6. なぜLuviraなのか */}
      <SectionWrapper>
        <SectionHeading eyebrow="Why Luvira" title={content.whyTitle} />
        <Reveal className="mt-12 md:mt-16 max-w-4xl mx-auto">
          <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-3xl p-8 md:p-12">
            {content.why.map((p, i) => (
              <p key={i} className={i === 0 ? 'text-[#e8e8f0] text-base md:text-lg leading-loose' : 'mt-4 text-[#e8e8f0] text-base md:text-lg leading-loose'}>
                {p}
              </p>
            ))}
          </div>
        </Reveal>
      </SectionWrapper>

      {/* 7. 導入事例 / 開発事例 */}
      <SectionWrapper className="bg-[#12121f]">
        <SectionHeading eyebrow="Cases" title={casesTitle} />
        {content.cases && content.cases.length > 0 ? (
          <div className="mt-12 md:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {content.cases.map((c, i) => (
              <Reveal key={c.title} delay={i * 60}>
                <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-7 h-full flex flex-col group transition-all duration-300 hover:-translate-y-1 hover:border-[#7c3aed] hover:shadow-[0_0_20px_rgba(124,58,237,0.2)]">
                  <div className="icon-outline shrink-0">
                    <i className={c.icon}></i>
                  </div>
                  <h3 className="mt-5 text-lg font-extrabold text-white leading-snug">{c.title}</h3>
                  <p className="mt-3 text-sm text-[#a0a0b0] leading-relaxed">{c.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        ) : (
          <Reveal className="mt-12 md:mt-16 max-w-3xl mx-auto">
            <div className="bg-[#1a1a2e] border border-dashed border-[#2a2a3e] rounded-3xl p-10 md:p-14 text-center">
              <div className="icon-outline mx-auto">
                <i className="ri-time-line"></i>
              </div>
              <h3 className="mt-6 text-lg font-extrabold text-white">Coming Soon</h3>
              <p className="mt-3 text-sm text-[#a0a0b0] leading-relaxed">
                {content.casesDescription ?? '導入事例は現在準備中です。準備が整い次第、順次公開いたします。'}
              </p>
            </div>
          </Reveal>
        )}
      </SectionWrapper>

      {/* 8. 導入までの流れ */}
      <SectionWrapper>
        <SectionHeading eyebrow="Process" title={flowTitle} />
        <div className="mt-12 md:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {content.flow.map((f, i) => (
            <Reveal key={f.title} delay={i * 60}>
              <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-7 h-full relative transition-all duration-300 hover:-translate-y-1 hover:border-[#7c3aed] hover:shadow-[0_0_20px_rgba(124,58,237,0.2)]">
                <div className="text-3xl font-extrabold text-[#a855f7]">{f.step}</div>
                <div className="mt-3 w-10 h-0.5 bg-[#a855f7]"></div>
                <h3 className="mt-4 text-lg font-extrabold text-white">{f.title}</h3>
                <ul className="mt-3 space-y-2">
                  {f.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-[#a0a0b0] leading-relaxed">
                      <span className="w-1.5 h-1.5 mt-2 rounded-full bg-[#a855f7]/50 shrink-0"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </SectionWrapper>

      {/* 追加セクション */}
      {content.extra && content.extra.length > 0 && (
        <SectionWrapper className="bg-[#12121f]">
          <div className="max-w-4xl mx-auto space-y-6">
            {content.extra.map((e, i) => (
              <Reveal key={e.title} delay={i * 80}>
                <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-8 md:p-10">
                  <h2 className="text-xl md:text-2xl font-extrabold text-white">{e.title}</h2>
                  <p className="mt-4 text-[#a0a0b0] text-base leading-loose">{e.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </SectionWrapper>
      )}

      {/* 9. FAQ */}
      {content.faq && content.faq.length > 0 && (
        <SectionWrapper className={content.extra && content.extra.length > 0 ? '' : 'bg-[#12121f]'}>
          <SectionHeading eyebrow="FAQ" title="よくある質問" />
          <Reveal className="mt-12 md:mt-16 max-w-3xl mx-auto">
            <FAQAccordion items={content.faq} />
          </Reveal>
        </SectionWrapper>
      )}

      {/* 10. CTA */}
      <CTASection
        title={content.cta.title}
        description={content.cta.description}
        buttonText={content.cta.buttonText}
        to={contactTo}
      />
    </>
  );
}